/**
 * Copyright (c) 2019-2026 ForgeRock. All rights reserved.
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file for details.
 */

import axios from 'axios';
import store from '@/store';
import getFQDN from '../utils/getFQDN';

/**
  * Generates an IDM Axios API instance
  * @param {object} requestOverride Takes an object of AXIOS parameters that can be used to either add
  * on extra information or override default properties https://github.com/axios/axios#request-config
  *
  * @returns {AxiosInstance}
  */
export function generateIdmApi(requestOverride = {}, routeToForbidden = true) {
  const requestDetails = {
    baseURL: store.state.SharedStore.idmBaseURL,
    headers: {},
    ...requestOverride,
  };

  if (store.state.SharedStore.idmOnly) {
    requestDetails.headers['x-requested-with'] = 'XMLHttpRequest';
  }

  // Check if web storage exists before trying to use it - see IAM-1873
  if (store.state.SharedStore.webStorageAvailable && sessionStorage.getItem('accessToken')) {
    requestDetails.headers.Authorization = `Bearer ${sessionStorage.getItem('accessToken')}`;
  }

  const request = axios.create(requestDetails);

  request.interceptors.response.use(null, (error) => {
    // Handle 401 session expiration in IDM-only mode
    if (store.state.SharedStore.idmOnly && error.response?.status === 401) {
      window.location.hash = '#/login';
      return Promise.reject(error);
    }

    // The journeys page is accessible to users with realm-admin priviledges,
    // however as it makes a call to openidm in order to access the Identities
    // Objects, this causes a 403 if the user doesn't also have openidm-admin
    // priviledges. This is a temporary check to stop the redirect to forbidden
    // in that scenario. This check should be removed when a workaround has been found.
    const { pathname } = new URL(getFQDN(error.response?.config.url));
    const { currentPackage } = store.state.SharedStore;
    if (pathname !== '/openidm/config/managed' && window.location.hash !== '#/journeys' && currentPackage !== 'enduser') {
      if (routeToForbidden && error.response?.status === 403) {
        window.location.hash = '#/forbidden';
        window.location.replace(window.location);
      }
      return Promise.reject(error);
    }

    // Used to verify IDM user privileges on the enduser package when the
    // /enduser/managed/{realm}_user call is rejected with a 403 error.
    if (currentPackage === 'enduser'
      && pathname.includes(`/enduser/managed/${store.state.realm}_user/`)
      && error.response?.status === 403) {
      return Promise.reject(error);
    }

    return false;
  });

  return request;
}
/**
  * Generates an AM Axios API instance
  * @param {object} resource Takes an object takes a resource object. example:
  * @param {object} requestOverride Takes an object of AXIOS parameters that can be used to either
  * add on extra information or override default properties https://github.com/axios/axios#request-config
  *
  * @returns {AxiosInstance}
  */
export function generateAmApi(resource, requestOverride = {}, routeToForbidden = true) {
  let headers = {
    'content-type': 'application/json',
    'accept-api-version': resource.apiVersion,
  };
  if (requestOverride.headers) {
    headers = {
      ...headers,
      ...requestOverride.headers,
    };
  }

  const requestDetails = {
    baseURL: `${store.state.SharedStore.amBaseURL}/json/${resource.path}`,
    ...requestOverride,
    headers,
  };

  const request = axios.create(requestDetails);

  request.interceptors.response.use(null, (error) => {
    if (routeToForbidden && error?.response?.status === 403) {
      window.location.hash = '#/forbidden';
      window.location.replace(window.location);
    }
    return Promise.reject(error);
  });

  return request;
}
/**
 * Generates a FRaaS Log API key API Axios instance
 * @param {object} requestOverride Takes an object of AXIOS parameters that can be used to either add
 * on extra information or override default properties https://github.com/axios/axios#request-config
 *
 * @returns {AxiosInstance}
 */
export function generateFraasLogApiKeyApi(requestOverride = {}) {
  const requestDetails = {
    baseURL: store.state.SharedStore.fraasLoggingKeyURL,
    headers: {},
    ...requestOverride,
  };

  return axios.create(requestDetails);
}

export function generateHelixApi(requestOverride = {}) {
  const requestDetails = {
    baseURL: store.state.SharedStore.helixEnvironmentUrl,
    headers: {
      'Content-Type': 'application/json',
    },
    ...requestOverride,
  };

  return axios.create(requestDetails);
}

/**
 * Returns the CDN base URL for application templates.
 * When air-gapped, returns a relative path for local serving.
 * Otherwise uses the development template CDN when `devAppTemplatesEnabled` is true,
 * or defaults to the production template CDN.
 * @returns {string} CDN base URL ending with a trailing slash.
 */
export function getCdnBaseURL() {
  if (store.state.SharedStore.isAirGapped) return `${process.env.BASE_URL}app-templates/`;
  return store.state.SharedStore.devAppTemplatesEnabled
    ? 'https://cdn.forgerock.com/platform/app-templates-development/'
    : 'https://cdn.forgerock.com/platform/app-templates/';
}

/**
 * Generates an Iga API Axios instance
 * @param {object} requestOverride Takes an object of AXIOS parameters that can be used to either add
 * on extra information or override default properties https://github.com/axios/axios#request-config
 *
 * @returns {AxiosInstance}
 */
export function generateIgaApi(requestOverride = {}) {
  const requestDetails = {
    baseURL: store.state.SharedStore.igaApiUrl,
    headers: {},
    ...requestOverride,
  };

  return axios.create(requestDetails);
}

/**
 * Generates a CDN API Axios instance
 * @param {object} requestOverride Takes an object of AXIOS parameters that can be used to either add
 * on extra information or override default properties https://github.com/axios/axios#request-config
 *
 * @returns {AxiosInstance}
 */
export function generateCdnApi(requestOverride = {}) {
  const requestDetails = {
    baseURL: getCdnBaseURL(),
    headers: { 'Content-Encoding': 'gzip' },
    ...requestOverride,
  };

  return axios.create(requestDetails);
}

/**
 * Generates an IGA Workflow API Axios instance
 * @param {object} requestOverride Takes an object of AXIOS parameters that can be used to either add
 * on extra information or override default properties https://github.com/axios/axios#request-config
 *
 * @returns {AxiosInstance}
 */
export function generateIgaOrchestrationApi(requestOverride = {}) {
  const requestDetails = {
    baseURL: store.state.SharedStore.igaOrchestrationApiUrl,
    headers: {},
    ...requestOverride,
  };

  return axios.create(requestDetails);
}

/**
 * Generates a FRaaS Environment API Axios instance
 * @param {object} requestOverride Takes an object of AXIOS parameters that can be used to either add
 * on extra information or override default properties https://github.com/axios/axios#request-config
 *
 * @returns {AxiosInstance}
 */
export function generateFraasEnvironmentApi(requestOverride = {}) {
  const requestDetails = {
    baseURL: store.state.SharedStore.fraasEnvironmentUrl,
    headers: {},
    ...requestOverride,
  };

  return axios.create(requestDetails);
}
/**
 * Generates a FRaaS monitoring API Axios instance
 * @param {object} requestOverride Takes an object of AXIOS parameters that can be used to either add
 * on extra information or override default properties https://github.com/axios/axios#request-config
 *
 * @returns {AxiosInstance}
 */
export function generateFraasMonitoringApi(requestOverride = {}) {
  const requestDetails = {
    baseURL: store.state.SharedStore.fraasMonitoringURL,
    headers: {},
    ...requestOverride,
  };

  const request = axios.create(requestDetails);

  request.interceptors.response.use(null, (error) => {
    if (error?.response?.status === 401) {
      window.logout();
    }
    return Promise.reject(error);
  });

  return request;
}

/**
 * Generates an Analytics API Axios instance
 * @param {object} requestOverride Takes an object of AXIOS parameters that can be used to either add
 * on extra information or override default properties https://github.com/axios/axios#request-config
 *
 * @returns {AxiosInstance}
 */
export function generateAnalyticsApi(requestOverride = {}) {
  const requestDetails = {
    baseURL: store.state.SharedStore.analyticsURL,
    headers: {},
    ...requestOverride,
  };

  return axios.create(requestDetails);
}

/**
 * Generates an Analytics Reports API Axios instance
 * @param {object} requestOverride Takes an object of AXIOS parameters that can be used to either add
 * on extra information or override default properties https://github.com/axios/axios#request-config
 *
 * @returns {AxiosInstance} Axios instance configured with 60-second timeout to prevent infinite loading states
 */
export function generateAutoAccessReports(requestOverride = {}) {
  const tenantId = process.env.VUE_APP_AUTO_ACCESS_TENANT_ID;
  const requestDetails = {
    baseURL: store.state.SharedStore.autoAccessReportsUrl,
    timeout: 60000,
    headers: {
      'X-TENANT-ID': tenantId,
      'Accept-API-Version': 'resource=1.0',
    },
    ...requestOverride,
  };

  return axios.create(requestDetails);
}

/**
 * Generates a FRaaS promotion API Axios instance
 * @param {object} requestOverride Takes an object of AXIOS parameters that can be used to either add
 * on extra information or override default properties https://github.com/axios/axios#request-config
 *
 * @returns {AxiosInstance}
 */
export function generateFraasPromotionApi(requestOverride = {}) {
  const requestDetails = {
    baseURL: store.state.SharedStore.fraasPromotionUrl,
    headers: {
      'accept-api-version': 'protocol=1.0,resource=1.0',
    },
    ...requestOverride,
  };

  return axios.create(requestDetails);
}

/**
 * Generates a PingFederate API Axios instance
 * Version will not change, safe to hardcode to 1
 * We will only ever make calls against the Admin APIs, safe to hardcode the path
 * newApi is the consolidated API, and will eventually be the only option - leaving original API
 * until custom-wsfed is updated to use the new endpoint
 * @param {boolean} newApi whether to use the new consolidated API - false by default
 * @param {object} requestOverride Takes an object of AXIOS parameters that can be used to either add
 * on extra information or override default properties https://github.com/axios/axios#request-config
 *
 * @returns {AxiosInstance}
 */
export function generatePingFederateApi(requestOverride = {}, newApi = false) {
  const path = newApi ? 'pf-aic/ws' : 'pf-admin-api';
  const requestDetails = {
    baseURL: `${store.state.SharedStore.pingFederateUrl}/${path}/v1`,
    headers: {
      accept: 'application/json',
      'X-XSRF-Header': 'PingFederate',
    },
    ...requestOverride,
  };

  return axios.create(requestDetails);
}
